import React, { useMemo, useState, useEffect, useRef } from "react";
import { Calculator } from "lucide-react";
import { L, tr } from "@/lib/i18n";
import { resolvePlansForUnit, defaultPlanFor, computeBreakdown, formatMoney } from "@/lib/paymentPlan";
import { resolveUnitValue, toNumberOrNull } from "@/lib/unitFinder";

/**
 * PaymentPlanSection — Phase B Feature 6.
 *
 * Renders inside Unit Detail ONLY when:
 *   - the unit has a price (`fields.price` after synonym lookup)
 *   - the project has at least one payment plan OR the unit has an override
 *
 * Buyer can switch between plans via the chip row. Selected plan id is
 * lifted via `onPlanSelected` so the lead-capture form attaches it.
 */
function pickPrice(unit, fieldDefinitions = []) {
  const definition = fieldDefinitions.find((fd) => fd.key === "price");
  if (definition) return toNumberOrNull(resolveUnitValue(unit, definition));
  if (unit.price != null) return toNumberOrNull(unit.price);
  const fields = unit.fields || {};
  const candidates = ["price", "Price", "PRICE", "Price (USD)", "Price (EUR)", "Price USD", "price_usd"];
  for (const k of candidates) {
    if (fields[k] != null && fields[k] !== "") return toNumberOrNull(fields[k]);
  }
  return null;
}

function pickCurrency(unit, fieldDefinitions = []) {
  const definition = fieldDefinitions.find((fd) => fd.key === "currency");
  if (definition) return resolveUnitValue(unit, definition) || "";
  const fields = unit.fields || {};
  return unit.currency || fields.currency || fields.Currency || fields.ccy || "";
}

export default function PaymentPlanSection({ unit, project, lang, defaultLang, onPlanSelected, fieldDefinitions = [] }) {
  const price = pickPrice(unit, fieldDefinitions);
  const currency = pickCurrency(unit, fieldDefinitions);
  const plans = useMemo(() => resolvePlansForUnit(project, unit), [project, unit]);
  const initial = useMemo(() => defaultPlanFor(project, unit), [project, unit]);
  const [selection, setSelection] = useState({ unitId: unit.id, planId: initial?.id || null });
  const selectedId = selection.unitId === unit.id ? selection.planId : initial?.id;
  const plan = plans.find((p) => p.id === selectedId) || initial;
  const breakdown = useMemo(() => computeBreakdown(plan, price, currency), [plan, price, currency]);
  const selectedCallback = useRef(onPlanSelected);
  selectedCallback.current = onPlanSelected;
  const resolvedPlanId = breakdown ? plan?.id || null : null;
  useEffect(() => {
    selectedCallback.current?.(resolvedPlanId);
  }, [unit.id, resolvedPlanId]);
  const money = (amount) => formatMoney(amount, breakdown?.currency, lang);

  if (!price || !plans.length || !breakdown) return null;

  const select = (p) => {
    setSelection({ unitId: unit.id, planId: p.id });
  };

  return (
    <div className="mt-6 pt-5 border-t border-white/10" data-testid="payment-plan-section">
      <div className="flex items-center gap-2 mb-3">
        <Calculator className="h-3.5 w-3.5 text-white/40" />
        <div className="text-[10px] uppercase tracking-[0.25em] text-white/50">{L(lang, "payment_plan")}</div>
      </div>

      {plans.length > 1 && (
        <div className="flex flex-wrap gap-1.5 mb-4" data-testid="payment-plan-chips">
          {plans.map((p) => {
            const active = p.id === plan.id;
            const planName = tr(p, "name", lang, defaultLang) || p.name || L(lang, "payment_plan");
            return (
              <button
                key={p.id}
                onClick={() => select(p)}
                data-testid={`payment-plan-chip-${p.id}`}
                className={`text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-full transition ${active ? "bg-white text-zinc-900" : "border border-white/15 text-white/70 hover:bg-white/5"}`}
              >
                {planName}
              </button>
            );
          })}
        </div>
      )}

      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        {breakdown.discount > 0 && (
          <KV label={L(lang, "list_price")} value={money(breakdown.list_price)} />
        )}
        {breakdown.discount > 0 && (
          <KV label={L(lang, "discount")} value={`− ${money(breakdown.discount)}`} accent />
        )}
        {breakdown.vat > 0 && (
          <KV label={`${L(lang, "vat")} (${breakdown.vat_pct}%)`} value={money(breakdown.vat)} />
        )}
        <KV label={L(lang, "total")} value={money(breakdown.total)} strong testid="pp-total" />
        {!breakdown.is_cash && breakdown.down_payment > 0 && (
          <KV label={`${L(lang, "down_payment")} (${breakdown.down_payment_pct}%)`}
              value={money(breakdown.down_payment)} testid="pp-dp" />
        )}
        {breakdown.remaining > 0 && breakdown.months > 0 && (
          <KV label={L(lang, "remaining")} value={money(breakdown.remaining)} />
        )}
        {breakdown.months > 0 && (!breakdown.has_final_adjustment || breakdown.months > 1) && (
          <KV label={`${L(lang, "monthly")} × ${breakdown.has_final_adjustment ? breakdown.months - 1 : breakdown.months}`}
              value={money(breakdown.monthly)} strong accent testid="pp-monthly" />
        )}
        {breakdown.has_final_adjustment && (
          <KV label={L(lang, "final_installment")} value={money(breakdown.final_monthly)} testid="pp-final-monthly" />
        )}
      </div>

      {plan.notes && (
        <div className="mt-3 text-xs text-white/50">{tr(plan, "notes", lang, defaultLang) || plan.notes}</div>
      )}
      <div className="mt-3 text-[10px] uppercase tracking-[0.2em] text-white/30">{L(lang, "pp_disclaimer")}</div>
    </div>
  );
}

function KV({ label, value, strong, accent, testid }) {
  return (
    <>
      <div className="text-white/50 text-xs">{label}</div>
      <div
        className={`text-right ${strong ? "font-display text-lg" : "text-sm"} ${accent ? "text-[#C4A47C]" : "text-white"}`}
        data-testid={testid}
      >
        {value}
      </div>
    </>
  );
}
